import { html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_PHASR_TOP_RECOMMENDATIONS } from 'apps/webcomponents/src/webcomponents.constants';
import { GzGenericWidgetContent } from 'apps/webcomponents/src/genericDashboard/gzGenericWidget';
import { PhasrTopRecommendationsPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { WidgetStateFrontEnd } from 'apps/webcomponents/src/genericDashboard/gzGenericWidget/types';
import { isEmpty } from 'lodash';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { classMap } from 'lit/directives/class-map.js';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../ui/gzButton/types';
import { ColumnKeys } from '../../../../phasrRecommendations/phasrRecommendationsGrid/types/grid';
import { PhasrRecommendationSideMenuWebComponent } from '../../../../phasrSideMenus/phasrRecommendationSideMenu';
import { DashboardEventsListeners } from '../../../../genericDashboard/types/types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { CategoryTypes } from '../../../../types/common/phasrTypes';
import { RESTRICT_ACTION } from '../../../../phasrRecommendations/phasrRecommendationsGrid/types';

@customElement(WEB_COMPONENT_PHASR_TOP_RECOMMENDATIONS)
export class PhasrTopRecommendationsWebComponent extends GzGenericWidgetContent {
  @property({type: Object, attribute: true, reflect: true})
  payload: PhasrTopRecommendationsPayload;

  private readonly sectionStrings = ['global', 'group'];
  private readonly sectionNrOfItems = 4;
  private readonly tooltipItemString = 'Device: {value}, User: {value}';
  private dictionary = [];

  static get styles() {
    return styles;
  }

  public willUpdate(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.willUpdate(_changedProperties);
    this.dictionary = Application.Main.getPhasrDictionary();
  }

  protected renderLoader(): TemplateResult {
    return html`${this.sectionStrings.map(section => this.renderSection(section))}`;
  }

  public injectDependencies(dependencies: any): void {
    this.dependencies = dependencies;
  }

  renderContent(): TemplateResult {
    let button = html``;
    if (this.state !== WidgetStateFrontEnd.IS_EMPTY) {
      button = html`
        <gz-button
        id='viewAllButton'
        class='d-flex align-items-end'
        type='${ButtonTypes.QUIET}'
        buttonheight='${ButtonHeights.SMALL}'
        buttonfontsize='${ButtonFontSizes.MEDIUM}'
        lowercase
        @click=${() => this.handleViewAllClick()}
      >
        ${translateFn('View all')}
      </gz-button>
      `;
    }

    return html`${this.sectionStrings.map(section => this.renderSection(section))}
    <div class='section' id='viewAllButtonWrapper'>
      ${button}
    </div>`;
  }

  private handleViewAllClick(): void {
    const allCategories = Object.values(CategoryTypes).filter(value => typeof value === 'number');

    const params: {
      filters: Partial<Record<ColumnKeys, unknown>>[];
    } = {
      filters: [
        {
          [ColumnKeys.COMPANY]: { exactMatch: this.payload.companyId },
        },
        {
          [ColumnKeys.CREATED_ON]: {
            timeRange: {
              kind: TimeRangeKind.ALL_TO_DATE,
            }
          }
        },
        {
          [ColumnKeys.PHASR_CATEGORIES]: { in: allCategories }
        }
      ]
    };
    Application.lib.Navigation.openAppPageInNewTab('/phasr-recommendations', params);
  }

  private renderSection(section: string): TemplateResult {
    return html`
      <div class='section'>
        ${this.renderSectionTitle(section)}
        <div class='section-items'>
          ${Array.from({length: this.sectionNrOfItems}, (_, i) =>
            this.state === WidgetStateFrontEnd.IS_LOADING ? this.renderLoaderItem(i) : this.renderItem(i, section)
          )}
        </div>
      </div>
    `;
  }

  private renderSectionTitle(section: string): TemplateResult {
    return html`
      <div class='section-title'>${this.getSectionTitle(section)}</div>`;
  }

  private renderLoaderItem(index: number): TemplateResult {
    const loadingClasses = {
      'mt-1': index === 0,
      'mb-1': index !== this.sectionNrOfItems - 1,
    };
    return html`
      <div class='loading-section'>
        <gz-loader rows='1' class='${classMap(loadingClasses)}'></gz-loader>
      </div>
    `;
  }

  private handleItemClick(recommendationId: string | null): void {
    // will be done in the future when we have the object id of the recommendation
    if (!recommendationId) {
      return;
    }

    const recommendationDetailsComponent = new PhasrRecommendationSideMenuWebComponent();
    recommendationDetailsComponent.setRecommendationId(recommendationId);
    recommendationDetailsComponent.shouldGetDataFromBackend = true;
    recommendationDetailsComponent.injectDependencies(this.dependencies);
    recommendationDetailsComponent.start().catch(console.error);

    const eventDetail = {component: recommendationDetailsComponent};
    this.dispatchEvent(new CustomEvent(DashboardEventsListeners.WIDGET_DETAILS_CLICKED, {
      detail: eventDetail,
      bubbles: true,
      composed: true
    }));
  }

  private renderItem(index: number, section: string): TemplateResult {
    const {state, name, surfacePercentageReduction, userProfilesCount, recommendationId} = this.getItemData(index, section);

    return html`
      <gz-widget-section-with-icons-and-percentage-item
        name='${name}'
        percentageValue='${surfacePercentageReduction}'
        .state='${state}'
        .iconValue='${userProfilesCount}'
        .tooltipString='${this.tooltipItemString}'
      ></gz-widget-section-with-icons-and-percentage-item>
    `;
  }

  private getItemData(
    index: number,
    section: string
  ): { state: WidgetStateFrontEnd; name: string; surfacePercentageReduction: number; userProfilesCount: number; recommendationId: string|null } {
    if (this.state === WidgetStateFrontEnd.IS_EMPTY || this.isPayloadEmptyOrInvalid(index, section)) {
      return {
        state: WidgetStateFrontEnd.IS_EMPTY,
        name: '',
        surfacePercentageReduction: 0,
        userProfilesCount: 0,
        recommendationId: null
      };
    }

    const item = this.payload?.categoryRecommendations[section][index];
    return {
      state: this.state,
      name: this.dictionary[item.recommendationId]?.[item.recommendationType === RESTRICT_ACTION ? 'rule_recommendation' : 'rule_allow_name'] ?? 'N/A',
      surfacePercentageReduction: item.surfacePercentageReduction,
      userProfilesCount: item.userProfilesCount,
      recommendationId: item.recommendationId
    };
  }

  private isPayloadEmptyOrInvalid(index: number, section: string): boolean {
    return (
      isEmpty(this.payload) ||
      !this.payload?.categoryRecommendations[section] ||
      index >= this.payload?.categoryRecommendations[section]?.length
    );
  }

  private getSectionTitle(section: string): string {
    switch (section) {
      case 'global':
        return translateFn('High impact');
      case 'group':
        return translateFn('Medium impact');
      default:
        return '';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'phasr-top-recommendations': PhasrTopRecommendationsWebComponent;
  }
}
